// screen: FAST channels (v3 §11, control plane internal/fast) — linear channels assembled from the library and approved
// AI clips without an encoder: channel settings, schedule rules (dayparts, rotation, repeat limits, fillers, breaks),
// the week grid of a schedule (drag to reorder), the playlist checker and publishing, the "best of" channel, and live
// break-ins. Off by default: the platform switch, and every new channel starts off air.
import * as React from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useLingui } from '@lingui/react/macro';
import { AlertTriangle, CalendarRange, GripVertical, Radio, Trash2, Tv, X } from 'lucide-react';
import type { Scope } from '@viewstream/api-client';
import { Badge, Button, EmptyState, Field, Input, Select, Skeleton, Switch, Tabs, TabsContent, TabsList, TabsTrigger, useToast } from '@viewstream/ui';
import { fmtDateTime, fmtDuration, fmtTime } from '@viewstream/i18n';
import { appTitle } from '~/app/brand';
import { useTenant } from '~/app/tenant';
import { useLocale } from '~/app/locale';
import { can } from '~/app/permissions';
import { problemText } from '~/app/team';
import { useChannels } from '~/app/live';
import { useSections } from '~/app/presets';
import {
  blockedCheck, ORDERS, RATINGS, toEdit, useFastBestOfPreview, useFastBreakIns, useFastChannel, useFastChannels, useFastMutations, useFastSchedule,
  useFastSchedules, type FastBestOf, type FastBlock, type FastChannel, type FastCheck, type FastItem, type FastRules, type FastSchedule, type FastSource,
} from '~/app/fast';

interface FastSearch { id?: string; sid?: string }

export const Route = createFileRoute('/_layout/t/$slug/fast')({
  component: Screen,
  validateSearch: (s: Record<string, unknown>): FastSearch => ({
    ...(typeof s['id'] === 'string' && /^[0-9a-f-]{36}$/.test(s['id']) ? { id: s['id'] } : {}),
    ...(typeof s['sid'] === 'string' && /^[0-9a-f-]{36}$/.test(s['sid']) ? { sid: s['sid'] } : {}),
  }),
});

const WRITE = 'channels:write' as Scope;
const OPERATE = 'channels:operate' as Scope;

function Screen() {
  const { t } = useLingui();
  const { tenant } = useTenant();
  const search = Route.useSearch();
  const navigate = useNavigate();
  const list = useFastChannels(tenant.slug);
  React.useEffect(() => { document.title = appTitle(t`FAST channels`); }, [t]);
  const go = (next: FastSearch) => void navigate({ to: '/t/$slug/fast' as never, params: { slug: tenant.slug } as never, search: next as never });
  return (
    <div className="space-y-6">
      <div>
        <h1 className="flex items-center gap-2 text-2xl font-semibold"><Tv className="size-6" aria-hidden />{t`FAST channels`}</h1>
        <p className="mt-1 max-w-3xl text-sm text-fg-muted">{t`Linear channels made from your library and approved AI clips, without an encoder: build a week from rules, check it, publish it. A new channel stays off air until you switch it on.`}</p>
      </div>
      {list.isLoading ? <Skeleton className="h-40" /> : !list.data?.available ? (
        <EmptyState icon={<Tv className="size-8" aria-hidden />} title={t`FAST channels are not switched on for this platform yet.`} description={t`Ask your ViewStream contact to enable them.`} />
      ) : (
        <div className="grid gap-6 lg:grid-cols-[minmax(0,18rem)_minmax(0,1fr)]">
          <div className="space-y-4">
            <ul className="space-y-1" aria-label={t`FAST channels`}>
              {list.data.data.map((c) => (
                <li key={c.id}>
                  <button type="button" onClick={() => go({ id: c.id })} aria-current={search.id === c.id ? 'true' : undefined}
                    className={`flex w-full items-center justify-between gap-2 rounded-md px-3 py-2 text-start text-sm ${search.id === c.id ? 'bg-primary/10 text-primary' : 'hover:bg-surface-2'}`}>
                    <span className="truncate">{c.title || c.slug}</span>
                    <span className="flex gap-1">
                      {c.kind === 'best_of' ? <Badge tone="primary">{t`Best of`}</Badge> : null}
                      <Badge tone={c.enabled ? 'live' : 'neutral'}>{c.enabled ? t`On air` : t`Off air`}</Badge>
                    </span>
                  </button>
                </li>
              ))}
            </ul>
            {list.data.data.length === 0 ? <p className="text-sm text-fg-muted">{t`No FAST channel yet.`}</p> : null}
            <CreateChannel onCreated={(id) => go({ id })} />
          </div>
          {search.id ? <ChannelDetail key={search.id} id={search.id} sid={search.sid} onSchedule={(sid) => go({ id: search.id, sid })} onDeleted={() => go({})} />
            : <p className="text-sm text-fg-muted">{t`Pick a channel or create one.`}</p>}
        </div>
      )}
    </div>
  );
}

function CreateChannel({ onCreated }: { onCreated: (id: string) => void }) {
  const { t } = useLingui();
  const { tenant, scopes } = useTenant();
  const toast = useToast();
  const m = useFastMutations(tenant.slug);
  const [slug, setSlug] = React.useState('');
  const [title, setTitle] = React.useState('');
  const [kind, setKind] = React.useState<'scheduled' | 'best_of'>('scheduled');
  if (!can(scopes, WRITE)) return null;
  return (
    <form className="space-y-2 rounded-md border border-border p-3" onSubmit={(e) => {
      e.preventDefault();
      m.create.mutate({ slug, title, kind }, {
        onSuccess: (c) => { setSlug(''); setTitle(''); onCreated(c.id); },
        onError: (err) => toast.push({ title: t`Could not create the channel`, description: problemText(err), tone: 'danger' }),
      });
    }}>
      <h2 className="text-sm font-semibold">{t`New FAST channel`}</h2>
      <Field id="fast-new-title" label={t`Name`}><Input value={title} onChange={(e) => setTitle(e.target.value)} /></Field>
      <Field id="fast-new-slug" label={t`Slug`} hint={t`Lowercase letters, digits and dashes; part of the stream URL.`}>
        <Input value={slug} onChange={(e) => setSlug(e.target.value.toLowerCase())} dir="ltr" required pattern="[a-z0-9][a-z0-9\-]{0,62}" />
      </Field>
      <Select ariaLabel={t`Kind`} value={kind} onValueChange={(v) => setKind(v as 'scheduled' | 'best_of')}
        options={[{ value: 'scheduled', label: t`Scheduled (weekly schedule from rules)` }, { value: 'best_of', label: t`Best of (approved AI clips)` }]} />
      <Button type="submit" size="sm" loading={m.create.isPending} disabled={!slug}>{t`Create`}</Button>
    </form>
  );
}

function ChannelDetail({ id, sid, onSchedule, onDeleted }: { id: string; sid?: string; onSchedule: (sid: string) => void; onDeleted: () => void }) {
  const { t } = useLingui();
  const { tenant } = useTenant();
  const { locale } = useLocale();
  const ch = useFastChannel(tenant.slug, id);
  if (ch.isLoading) return <Skeleton className="h-80" />;
  if (!ch.data) return <p className="text-sm text-danger">{t`Could not load the channel.`}</p>;
  const c = ch.data;
  const o = { locale, tz: c.timezone };
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-xl font-semibold">{c.title || c.slug}</h2>
        <Badge tone={c.enabled ? 'live' : 'neutral'}>{c.enabled ? t`On air` : t`Off air`}</Badge>
      </div>
      <div className="grid gap-2 rounded-md border border-border p-3 text-sm sm:grid-cols-2">
        <div><span className="text-fg-muted">{t`Now`}: </span>{c.break_in ? <Badge tone="live"><Radio className="size-3" aria-hidden />{t`Live break-in`}</Badge>
          : c.on_air ? <span>{c.on_air.title} <bdi className="ltr-isolate text-fg-muted">{fmtTime(c.on_air.start_at, o)}–{fmtTime(c.on_air.end_at, o)}</bdi></span> : <span className="text-fg-muted">{t`Nothing scheduled`}</span>}</div>
        <div><span className="text-fg-muted">{t`Next`}: </span>{c.next[0] ? <span>{c.next[0].title} <bdi className="ltr-isolate text-fg-muted">{fmtTime(c.next[0].start_at, o)}</bdi></span> : '—'}</div>
        <div className="sm:col-span-2"><span className="text-fg-muted">HLS: </span><bdi className="ltr-isolate break-all font-mono text-xs">{c.urls.hls}</bdi></div>
        {c.epg_enabled ? <div className="sm:col-span-2"><span className="text-fg-muted">{t`Guide`}: </span><bdi className="ltr-isolate break-all font-mono text-xs">{c.urls.epg_xmltv}</bdi></div> : null}
      </div>
      <Tabs defaultValue="schedule" dir={locale === 'he' ? 'rtl' : 'ltr'}>
        <TabsList>
          <TabsTrigger value="schedule">{t`Schedule`}</TabsTrigger>
          {c.kind === 'scheduled' ? <TabsTrigger value="rules">{t`Rules`}</TabsTrigger> : <TabsTrigger value="bestof">{t`Best of`}</TabsTrigger>}
          <TabsTrigger value="breakin">{t`Live break-in`}</TabsTrigger>
          <TabsTrigger value="settings">{t`Settings`}</TabsTrigger>
        </TabsList>
        <TabsContent value="schedule" className="pt-4"><SchedulePanel c={c} sid={sid} onSchedule={onSchedule} /></TabsContent>
        {c.kind === 'scheduled' ? <TabsContent value="rules" className="pt-4"><RulesPanel c={c} /></TabsContent>
          : <TabsContent value="bestof" className="pt-4"><BestOfPanel c={c} /></TabsContent>}
        <TabsContent value="breakin" className="pt-4"><BreakInPanel c={c} /></TabsContent>
        <TabsContent value="settings" className="pt-4"><SettingsPanel c={c} onDeleted={onDeleted} /></TabsContent>
      </Tabs>
      <p className="text-xs text-fg-muted">{t`Created`} <bdi className="ltr-isolate">{fmtDateTime(c.created_at, o)}</bdi></p>
    </div>
  );
}

// --- schedule: list, generate, week grid, check, publish ------------------------------------------------------------

/** The UTC instant of local midnight of a yyyy-mm-dd day in a time zone. */
function zonedMidnight(day: string, tz: string): string {
  const [y, m, d] = day.split('-').map(Number);
  const guess = Date.UTC(y ?? 1970, (m ?? 1) - 1, d ?? 1);
  const parts = new Intl.DateTimeFormat('en-US', { timeZone: tz, hourCycle: 'h23', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' }).formatToParts(new Date(guess));
  const get = (k: string) => Number(parts.find((p) => p.type === k)?.value ?? 0);
  const asUTC = Date.UTC(get('year'), get('month') - 1, get('day'), get('hour') % 24, get('minute'));
  return new Date(guess - (asUTC - guess)).toISOString();
}

function dayKey(iso: string, tz: string): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date(iso));
}

function nextSunday(): string {
  const d = new Date();
  d.setDate(d.getDate() + ((7 - d.getDay()) % 7 || 7));
  return d.toISOString().slice(0, 10);
}

function statusTone(s: FastSchedule['status']): 'ok' | 'danger' | 'neutral' | 'primary' | 'live' {
  return s === 'published' ? 'live' : s === 'checked' ? 'ok' : s === 'blocked' ? 'danger' : s === 'superseded' ? 'neutral' : 'primary';
}

function SchedulePanel({ c, sid, onSchedule }: { c: FastChannel; sid?: string; onSchedule: (sid: string) => void }) {
  const { t } = useLingui();
  const { tenant, scopes } = useTenant();
  const { locale } = useLocale();
  const toast = useToast();
  const m = useFastMutations(tenant.slug);
  const list = useFastSchedules(tenant.slug, c.id);
  const [day, setDay] = React.useState(nextSunday);
  const [days, setDays] = React.useState(7);
  const [warnings, setWarnings] = React.useState<string[]>([]);
  const o = { locale, tz: c.timezone };
  const statusLabel: Record<FastSchedule['status'], string> = { draft: t`Draft`, checked: t`Checked`, blocked: t`Blocked`, published: t`Published`, superseded: t`Replaced` };
  return (
    <div className="space-y-4">
      {c.kind === 'scheduled' && can(scopes, WRITE) ? (
        <form className="flex flex-wrap items-end gap-3" onSubmit={(e) => {
          e.preventDefault();
          m.generate.mutate({ id: c.id, start: zonedMidnight(day, c.timezone), days }, {
            onSuccess: (r) => {
              setWarnings(r.warnings);
              toast.push({ title: t`Schedule generated`, description: t`${r.schedule.items?.length ?? 0} items in ${r.schedule.generated_ms ?? 0} ms`, tone: 'ok' });
              onSchedule(r.schedule.id);
            },
            onError: (err) => toast.push({ title: t`Could not generate the schedule`, description: problemText(err), tone: 'danger' }),
          });
        }}>
          <Field id="fast-gen-day" label={t`First day`}><Input type="date" value={day} onChange={(e) => setDay(e.target.value)} dir="ltr" /></Field>
          <Field id="fast-gen-days" label={t`Days`}><Input type="number" min={1} max={14} value={days} onChange={(e) => setDays(Number(e.target.value) || 7)} dir="ltr" /></Field>
          <div className="mb-4"><Button type="submit" loading={m.generate.isPending}><CalendarRange className="size-4" aria-hidden />{t`Generate from the rules`}</Button></div>
        </form>
      ) : null}
      {warnings.length > 0 ? (
        <ul className="space-y-1 rounded-md border border-warn/30 bg-warn/10 p-3 text-sm">{warnings.map((w) => <li key={w}>{w}</li>)}</ul>
      ) : null}
      {list.isLoading ? <Skeleton className="h-16" /> : (
        <div className="flex flex-wrap gap-2" role="list" aria-label={t`Schedules`}>
          {(list.data?.data ?? []).map((s) => (
            <Button key={s.id} role="listitem" size="sm" variant={sid === s.id ? 'primary' : 'secondary'} onClick={() => onSchedule(s.id)}>
              <bdi className="ltr-isolate">{fmtDateTime(s.starts_at, o, { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}</bdi>
              <Badge tone={statusTone(s.status)}>{statusLabel[s.status]}</Badge>
            </Button>
          ))}
          {list.data?.data.length === 0 ? <p className="text-sm text-fg-muted">{t`No schedule yet.`}</p> : null}
        </div>
      )}
      {sid ? <ScheduleView key={sid} c={c} sid={sid} /> : null}
    </div>
  );
}

function ScheduleView({ c, sid }: { c: FastChannel; sid: string }) {
  const { t } = useLingui();
  const { tenant, scopes } = useTenant();
  const { locale } = useLocale();
  const toast = useToast();
  const m = useFastMutations(tenant.slug);
  const sc = useFastSchedule(tenant.slug, c.id, sid);
  const [check, setCheck] = React.useState<FastCheck | null>(null);
  const [drag, setDrag] = React.useState<string | null>(null);
  const items = React.useMemo(() => sc.data?.items ?? [], [sc.data]);
  const o = { locale, tz: c.timezone };
  if (sc.isLoading) return <Skeleton className="h-64" />;
  if (!sc.data) return null;
  const s = sc.data;
  const editable = s.status !== 'published' && s.status !== 'superseded' && can(scopes, WRITE);
  const shown = check ?? (s.check && typeof s.check.ok === 'boolean' ? (s.check as FastCheck) : null);
  const save = (next: FastItem[]) => m.putItems.mutate({ id: c.id, sid, items: toEdit(next) }, {
    onSuccess: () => { setCheck(null); void sc.refetch(); },
    onError: (err) => toast.push({ title: t`Could not save the order`, description: problemText(err), tone: 'danger' }),
  });
  const move = (from: string, to: string) => {
    if (from === to) return;
    const next = items.filter((i) => i.id !== from);
    const at = next.findIndex((i) => i.id === to);
    const it = items.find((i) => i.id === from);
    if (!it || at < 0) return;
    next.splice(at, 0, it);
    save(next);
  };
  // the replacement keeps its own length (end = start: toEdit sends no duration, the server uses the asset's)
  const replace = (itemId: string, assetId: string, title: string) => save(items.map((i) => (i.id === itemId ? { ...i, asset_id: assetId, title, end_at: i.start_at } : i)));
  const byDay = new Map<string, FastItem[]>();
  for (const it of items) {
    const k = dayKey(it.start_at, c.timezone);
    byDay.set(k, [...(byDay.get(k) ?? []), it]);
  }
  const kindLabel = { asset: t`Programme`, filler: t`Filler`, break: t`Break`, clip: t`Clip` } as const;
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <Badge tone={statusTone(s.status)}>{s.status}</Badge>
        <span className="text-sm text-fg-muted"><bdi className="ltr-isolate">{fmtDateTime(s.starts_at, o)} – {fmtDateTime(s.ends_at, o)}</bdi> · {t`${items.length} items`}</span>
        <span className="ms-auto flex flex-wrap gap-2">
          {can(scopes, WRITE) && s.status !== 'superseded' ? (
            <Button size="sm" variant="secondary" loading={m.check.isPending} onClick={() => m.check.mutate({ id: c.id, sid, deep: true }, {
              onSuccess: (r) => { setCheck(r); void sc.refetch(); },
              onError: (err) => toast.push({ title: t`The check failed`, description: problemText(err), tone: 'danger' }),
            })}>{t`Check`}</Button>
          ) : null}
          {can(scopes, OPERATE) && editable ? (
            <Button size="sm" loading={m.publish.isPending} onClick={() => m.publish.mutate({ id: c.id, sid }, {
              onSuccess: (r) => { setCheck(r.check); void sc.refetch(); toast.push({ title: t`Published`, description: fmtDateTime(r.on_air_from, o), tone: 'ok' }); },
              onError: (err) => {
                const b = blockedCheck(err);
                if (b) setCheck(b);
                toast.push({ title: b ? t`Blocked by the playlist checker` : t`Could not publish`, description: b ? t`${b.errors} errors: fix them and publish again.` : problemText(err), tone: 'danger' });
                void sc.refetch();
              },
            })}>{t`Publish`}</Button>
          ) : null}
          {editable ? (
            <Button size="sm" variant="ghost" aria-label={t`Delete schedule`} onClick={() => m.removeSchedule.mutate({ id: c.id, sid })}><Trash2 className="size-4" aria-hidden /></Button>
          ) : null}
        </span>
      </div>
      {shown ? <CheckView check={shown} items={items} tz={c.timezone} onReplace={editable ? replace : undefined} /> : null}
      {editable ? <p className="text-xs text-fg-muted">{t`Drag an item onto another to move it there; the schedule is laid out again from its start without gaps.`}</p> : null}
      <div className="grid gap-2 overflow-x-auto md:grid-cols-7">
        {[...byDay.entries()].map(([d, its]) => (
          <section key={d} className="min-w-0 space-y-1 rounded-md border border-border p-2" aria-label={d}>
            <h3 className="text-xs font-semibold"><bdi className="ltr-isolate">{fmtDateTime(its[0]!.start_at, o, { weekday: 'short', day: '2-digit', month: '2-digit' })}</bdi></h3>
            <ol className="space-y-1">
              {its.map((it) => {
                const err = it.issues.some((i) => i.severity === 'error');
                const warn = !err && it.issues.length > 0;
                return (
                  <li key={it.id} draggable={editable} onDragStart={() => setDrag(it.id)} onDragOver={(e) => { if (editable) e.preventDefault(); }}
                    onDrop={(e) => { e.preventDefault(); if (drag) move(drag, it.id); setDrag(null); }}
                    className={`group rounded border px-1.5 py-1 text-xs ${err ? 'border-danger/50 bg-danger/10' : warn ? 'border-warn/50 bg-warn/10' : it.kind === 'break' ? 'border-border bg-surface-2' : 'border-border'} ${drag === it.id ? 'opacity-50' : ''}`}
                    title={it.issues.map((i) => i.message).join('\n') || undefined}>
                    <div className="flex items-center gap-1">
                      {editable ? <GripVertical className="size-3 shrink-0 cursor-grab text-fg-muted" aria-hidden /> : null}
                      <bdi className="ltr-isolate text-fg-muted">{fmtTime(it.start_at, o)}</bdi>
                      {it.kind !== 'asset' ? <span className="text-fg-muted">· {kindLabel[it.kind]}</span> : null}
                      {err || warn ? <AlertTriangle className={`size-3 ${err ? 'text-danger' : 'text-warn'}`} aria-label={err ? t`Error` : t`Warning`} /> : null}
                      {editable ? (
                        <button type="button" className="ms-auto hidden text-fg-muted group-hover:inline" aria-label={t`Remove`} onClick={() => save(items.filter((x) => x.id !== it.id))}>
                          <X className="size-3" aria-hidden />
                        </button>
                      ) : null}
                    </div>
                    <div className="truncate">{it.title || '—'}</div>
                    <div className="text-fg-muted">{fmtDuration(Date.parse(it.end_at) - Date.parse(it.start_at))}{it.block ? ` · ${it.block}` : ''}</div>
                  </li>
                );
              })}
            </ol>
          </section>
        ))}
      </div>
    </div>
  );
}

function CheckView({ check, items, tz, onReplace }: { check: FastCheck; items: FastItem[]; tz: string; onReplace?: (item: string, asset: string, title: string) => void }) {
  const { t } = useLingui();
  const { locale } = useLocale();
  const o = { locale, tz };
  const title = (id?: string) => items.find((i) => i.id === id)?.title ?? '';
  return (
    <div className={`rounded-md border p-3 text-sm ${check.ok ? 'border-ok/30 bg-ok/10' : 'border-danger/30 bg-danger/10'}`} role="status">
      <p className="font-medium">{check.ok ? t`The playlist checker found no errors.` : t`Publishing is blocked: ${check.errors} errors.`} {check.warnings > 0 ? t`${check.warnings} warnings.` : ''}</p>
      {check.issues.length > 0 ? (
        <ul className="mt-2 max-h-72 space-y-1 overflow-y-auto">
          {check.issues.slice(0, 200).map((i, n) => (
            <li key={n} className="flex flex-wrap items-center gap-2">
              <Badge tone={i.severity === 'error' ? 'danger' : 'warn'}>{i.severity === 'error' ? t`Error` : t`Warning`}</Badge>
              {i.at ? <bdi className="ltr-isolate text-fg-muted">{fmtDateTime(i.at, o, { weekday: 'short', hour: '2-digit', minute: '2-digit' })}</bdi> : null}
              {i.item_id ? <span className="font-medium">{title(i.item_id)}</span> : null}
              <span>{i.message}</span>
              {i.suggest && onReplace && i.item_id ? (
                <Button size="sm" variant="link" onClick={() => onReplace(i.item_id!, i.suggest!.asset_id, i.suggest!.title)}>{t`Replace with “${i.suggest.title}”`}</Button>
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

// --- rules ------------------------------------------------------------------------------------------------------------

const csv = (v: string) => v.split(',').map((x) => x.trim()).filter(Boolean);

function SourceField({ id, label, value, onChange }: { id: string; label: string; value: FastSource | undefined; onChange: (v: FastSource) => void }) {
  const { t } = useLingui();
  const { tenant } = useTenant();
  const sections = useSections(tenant.slug);
  const v = value ?? {};
  return (
    <div className="grid gap-2 sm:grid-cols-2">
      <Field id={`${id}-tags`} label={t`${label}: tags`} hint={t`Comma-separated (metadata.tags of the videos)`}>
        <Input defaultValue={(v.tags ?? []).join(', ')} onBlur={(e) => onChange({ ...v, tags: csv(e.target.value) })} />
      </Field>
      <div className="mb-4">
        <span className="mb-1 block text-sm font-medium">{t`${label}: library section`}</span>
        <Select ariaLabel={t`${label}: library section`} value={v.collection_id ?? 'none'} onValueChange={(x) => onChange({ ...v, collection_id: x === 'none' ? undefined : x })}
          options={[{ value: 'none', label: t`None` }, ...(sections.data ?? []).map((s) => ({ value: s.id, label: s.name }))]} />
      </div>
    </div>
  );
}

function RulesPanel({ c }: { c: FastChannel }) {
  const { t } = useLingui();
  const { tenant, scopes } = useTenant();
  const toast = useToast();
  const m = useFastMutations(tenant.slug);
  const [r, setR] = React.useState<FastRules>(() => ({ ...c.rules, blocks: c.rules.blocks ?? [], fillers: c.rules.fillers ?? {}, break_source: c.rules.break_source ?? {} }));
  const days = [t`Sun`, t`Mon`, t`Tue`, t`Wed`, t`Thu`, t`Fri`, t`Sat`];
  const orderLabel: Record<string, string> = { rotation: t`Rotation (least recently played first)`, newest: t`Newest first`, random: t`Random`, sequential: t`In order (series)` };
  const setBlock = (i: number, b: Partial<FastBlock>) => setR({ ...r, blocks: r.blocks.map((x, n) => (n === i ? { ...x, ...b } : x)) });
  const num = (v: string) => (v === '' ? undefined : Number(v));
  const ro = !can(scopes, WRITE);
  return (
    <form className="space-y-4" onSubmit={(e) => {
      e.preventDefault();
      m.patch.mutate({ id: c.id, body: { rules: r } }, {
        onSuccess: () => toast.push({ title: t`Rules saved`, tone: 'ok' }),
        onError: (err) => toast.push({ title: t`Could not save the rules`, description: problemText(err), tone: 'danger' }),
      });
    }}>
      <fieldset disabled={ro} className="space-y-4">
        <h3 className="font-semibold">{t`Dayparts`}</h3>
        <p className="text-sm text-fg-muted">{t`Each block fills its hours from its source. The first matching block wins; an item may run a little past the end of its block rather than leave a gap.`}</p>
        {r.blocks.map((b, i) => (
          <div key={i} className="space-y-2 rounded-md border border-border p-3">
            <div className="grid gap-2 sm:grid-cols-4">
              <Field id={`blk-${i}-name`} label={t`Name`}><Input value={b.name} onChange={(e) => setBlock(i, { name: e.target.value })} /></Field>
              <Field id={`blk-${i}-from`} label={t`From`}><Input type="time" value={b.from} onChange={(e) => setBlock(i, { from: e.target.value })} dir="ltr" /></Field>
              <Field id={`blk-${i}-to`} label={t`To`} hint={t`24:00 = midnight`}><Input value={b.to} onChange={(e) => setBlock(i, { to: e.target.value })} dir="ltr" placeholder="24:00" /></Field>
              <div className="mb-4">
                <span className="mb-1 block text-sm font-medium">{t`Order`}</span>
                <Select ariaLabel={t`Order`} value={b.order ?? 'rotation'} onValueChange={(v) => setBlock(i, { order: v as FastBlock['order'] })} options={ORDERS.map((x) => ({ value: x, label: orderLabel[x] ?? x }))} />
              </div>
            </div>
            <div className="flex flex-wrap gap-3" role="group" aria-label={t`Days`}>
              {days.map((d, n) => (
                <label key={n} className="flex items-center gap-1 text-sm">
                  <input type="checkbox" checked={!b.days?.length || b.days.includes(n)}
                    onChange={(e) => {
                      const cur = b.days?.length ? b.days : [0, 1, 2, 3, 4, 5, 6];
                      const next = e.target.checked ? [...cur, n].sort() : cur.filter((x) => x !== n);
                      setBlock(i, { days: next.length === 7 ? [] : next });
                    }} />{d}
                </label>
              ))}
            </div>
            <SourceField id={`blk-${i}-src`} label={t`Source`} value={b.source} onChange={(v) => setBlock(i, { source: v })} />
            <div className="grid gap-2 sm:grid-cols-3">
              <div className="mb-4">
                <span className="mb-1 block text-sm font-medium">{t`Highest rating`}</span>
                <Select ariaLabel={t`Highest rating`} value={b.max_rating || 'any'} onValueChange={(v) => setBlock(i, { max_rating: v === 'any' ? undefined : v })}
                  options={[{ value: 'any', label: t`Any` }, ...RATINGS.map((x) => ({ value: x, label: x === 'all' ? t`All ages` : `${x}+` }))]} />
              </div>
              <Field id={`blk-${i}-min`} label={t`Shortest (s)`}><Input type="number" min={0} value={b.min_len_s ?? ''} onChange={(e) => setBlock(i, { min_len_s: num(e.target.value) })} dir="ltr" /></Field>
              <Field id={`blk-${i}-max`} label={t`Longest (s)`}><Input type="number" min={0} value={b.max_len_s ?? ''} onChange={(e) => setBlock(i, { max_len_s: num(e.target.value) })} dir="ltr" /></Field>
            </div>
            <Button size="sm" variant="ghost" onClick={() => setR({ ...r, blocks: r.blocks.filter((_, n) => n !== i) })}><Trash2 className="size-4" aria-hidden />{t`Remove block`}</Button>
          </div>
        ))}
        <Button size="sm" variant="secondary" onClick={() => setR({ ...r, blocks: [...r.blocks, { name: t`Block ${r.blocks.length + 1}`, from: '06:00', to: '12:00', source: {} }] })}>{t`Add block`}</Button>
        <h3 className="font-semibold">{t`Everything else`}</h3>
        <SourceField id="rules-default" label={t`Outside the blocks`} value={r.default} onChange={(v) => setR({ ...r, default: v })} />
        <SourceField id="rules-fillers" label={t`Fillers`} value={r.fillers} onChange={(v) => setR({ ...r, fillers: v })} />
        <div className="grid gap-2 sm:grid-cols-2">
          <Field id="rules-repeat" label={t`No repeat within (hours)`} hint={t`Default 6`}><Input type="number" min={0} max={336} value={r.repeat_hours ?? ''} onChange={(e) => setR({ ...r, repeat_hours: num(e.target.value) })} dir="ltr" /></Field>
          <Field id="rules-plays" label={t`Plays per day at most`} hint={t`Default 3`}><Input type="number" min={0} max={48} value={r.max_plays_per_day ?? ''} onChange={(e) => setR({ ...r, max_plays_per_day: num(e.target.value) })} dir="ltr" /></Field>
        </div>
        <h3 className="font-semibold">{t`Ad breaks`}</h3>
        <div className="grid gap-2 sm:grid-cols-2">
          <Field id="rules-brk-every" label={t`A break every (minutes of programmes)`} hint={t`0 = no breaks`}><Input type="number" min={0} max={240} value={r.break_every_min ?? ''} onChange={(e) => setR({ ...r, break_every_min: num(e.target.value) })} dir="ltr" /></Field>
          <Field id="rules-brk-max" label={t`Break length at most (s)`} hint={t`Default 120`}><Input type="number" min={0} max={900} value={r.break_max_s ?? ''} onChange={(e) => setR({ ...r, break_max_s: num(e.target.value) })} dir="ltr" /></Field>
        </div>
        <SourceField id="rules-brk" label={t`Break content (promos, slates)`} value={r.break_source} onChange={(v) => setR({ ...r, break_source: v })} />
        <p className="text-sm text-fg-muted">{t`Breaks carry SCTE-35 markers (CUE-OUT / CUE-IN) for FAST platforms; with SSAI on, ViewStream inserts the ads itself.`}</p>
        <Button type="submit" loading={m.patch.isPending}>{t`Save rules`}</Button>
      </fieldset>
    </form>
  );
}

// --- best of ----------------------------------------------------------------------------------------------------------

function BestOfPanel({ c }: { c: FastChannel }) {
  const { t } = useLingui();
  const { tenant, scopes } = useTenant();
  const toast = useToast();
  const m = useFastMutations(tenant.slug);
  const [b, setB] = React.useState<FastBestOf>(c.best_of ?? { approved: false });
  const preview = useFastBestOfPreview(tenant.slug, c.id, true);
  const num = (v: string) => (v === '' ? undefined : Number(v));
  const save = (next: FastBestOf) => m.patch.mutate({ id: c.id, body: { best_of: next } }, {
    onSuccess: () => { toast.push({ title: t`Saved`, tone: 'ok' }); void preview.refetch(); },
    onError: (err) => toast.push({ title: t`Could not save`, description: problemText(err), tone: 'danger' }),
  });
  return (
    <div className="space-y-4">
      <p className="text-sm text-fg-muted">{t`The channel plays approved AI clips by these rules, looped, and refreshes by itself once the rules are approved. Rejected or unapproved clips never play.`}</p>
      <fieldset disabled={!can(scopes, WRITE)} className="grid gap-2 sm:grid-cols-3">
        <Field id="bo-hours" label={t`Approved in the last (hours)`} hint={t`Default 24`}><Input type="number" value={b.hours ?? ''} onChange={(e) => setB({ ...b, hours: num(e.target.value) })} dir="ltr" /></Field>
        <Field id="bo-topics" label={t`Topics`} hint={t`Comma-separated; none = every topic`}><Input defaultValue={(b.topics ?? []).join(', ')} onBlur={(e) => setB({ ...b, topics: csv(e.target.value) })} /></Field>
        <div className="mb-4">
          <span className="mb-1 block text-sm font-medium">{t`Order`}</span>
          <Select ariaLabel={t`Order`} value={b.order ?? 'score'} onValueChange={(v) => setB({ ...b, order: v as FastBestOf['order'] })}
            options={[{ value: 'score', label: t`Best first (AI score)` }, { value: 'newest', label: t`Newest first` }]} />
        </div>
        <Field id="bo-min" label={t`Shortest (s)`}><Input type="number" value={b.min_len_s ?? ''} onChange={(e) => setB({ ...b, min_len_s: num(e.target.value) })} dir="ltr" /></Field>
        <Field id="bo-max" label={t`Longest (s)`}><Input type="number" value={b.max_len_s ?? ''} onChange={(e) => setB({ ...b, max_len_s: num(e.target.value) })} dir="ltr" /></Field>
        <Field id="bo-items" label={t`Clips at most`}><Input type="number" value={b.max_items ?? ''} onChange={(e) => setB({ ...b, max_items: num(e.target.value) })} dir="ltr" /></Field>
        <Field id="bo-refresh" label={t`Refresh every (minutes)`} hint={t`Default 60`}><Input type="number" value={b.refresh_min ?? ''} onChange={(e) => setB({ ...b, refresh_min: num(e.target.value) })} dir="ltr" /></Field>
        <Field id="bo-horizon" label={t`Schedule ahead (minutes)`} hint={t`Default 120`}><Input type="number" value={b.horizon_min ?? ''} onChange={(e) => setB({ ...b, horizon_min: num(e.target.value) })} dir="ltr" /></Field>
      </fieldset>
      <div className="flex flex-wrap items-center gap-3">
        {can(scopes, WRITE) ? <Button onClick={() => save(b)} loading={m.patch.isPending}>{t`Save rules`}</Button> : null}
        {can(scopes, OPERATE) ? (
          <label className="flex items-center gap-2 text-sm">
            <Switch checked={b.approved} onCheckedChange={(v) => { const next = { ...b, approved: v }; setB(next); save(next); }} />{t`Rules approved (the channel refreshes by itself)`}
          </label>
        ) : null}
        {can(scopes, OPERATE) ? (
          <Button variant="secondary" loading={m.refreshBestOf.isPending} onClick={() => m.refreshBestOf.mutate(c.id, {
            onSuccess: () => toast.push({ title: t`Programmed and published`, tone: 'ok' }),
            onError: (err) => toast.push({ title: blockedCheck(err) ? t`Blocked by the playlist checker` : t`Could not programme the channel`, description: problemText(err), tone: 'danger' }),
          })}>{t`Programme now`}</Button>
        ) : null}
      </div>
      <h3 className="font-semibold">{t`Clips the rules select`}</h3>
      {preview.isLoading ? <Skeleton className="h-24" /> : (
        <ol className="space-y-1 text-sm">
          {(preview.data?.data ?? []).map((x) => (
            <li key={x.artifact_id} className="flex gap-2"><span className="truncate">{x.title}</span><span className="text-fg-muted">{fmtDuration(x.duration_ms)} · {x.score.toFixed(1)}</span></li>
          ))}
          {preview.data?.data.length === 0 ? <li className="text-fg-muted">{t`No approved clip matches the rules (same ladder, length, topics).`}</li> : null}
        </ol>
      )}
    </div>
  );
}

// --- live break-in ----------------------------------------------------------------------------------------------------

function BreakInPanel({ c }: { c: FastChannel }) {
  const { t } = useLingui();
  const { tenant, scopes } = useTenant();
  const { locale } = useLocale();
  const toast = useToast();
  const m = useFastMutations(tenant.slug);
  const live = useChannels(tenant.slug);
  const bis = useFastBreakIns(tenant.slug, c.id);
  const [liveId, setLiveId] = React.useState(c.live_channel_id ?? '');
  const [start, setStart] = React.useState('');
  const [end, setEnd] = React.useState('');
  const o = { locale, tz: c.timezone };
  const opts = (live.data ?? []).filter((x) => (x as { ladder?: string }).ladder === c.ladder).map((x) => ({ value: x.id ?? '', label: x.title || x.slug || '' }));
  const running = (bis.data?.data ?? []).find((b) => !b.cancelled && Date.parse(b.start_at) <= Date.now() && (!b.end_at || Date.parse(b.end_at) > Date.now()));
  const go = (body: { start_at?: string; end_at?: string }) => m.breakIn.mutate({ id: c.id, live_channel_id: liveId || undefined, ...body }, {
    onSuccess: () => { toast.push({ title: body.start_at ? t`Break-in scheduled` : t`Switched to live`, tone: 'ok' }); void bis.refetch(); },
    onError: (err) => toast.push({ title: t`Could not switch`, description: problemText(err), tone: 'danger' }),
  });
  const canOp = can(scopes, OPERATE);
  return (
    <div className="space-y-4">
      <p className="text-sm text-fg-muted">{t`Switch the channel to a live channel for an event: viewers move to live within one segment and return to the schedule at the next slot after you end the break-in. The live channel must use the same ladder.`}</p>
      <div className="max-w-sm">
        <span className="mb-1 block text-sm font-medium">{t`Live channel`}</span>
        <Select ariaLabel={t`Live channel`} value={liveId || undefined} placeholder={t`Choose a live channel`} onValueChange={setLiveId} options={opts} />
      </div>
      {canOp ? (
        <div className="flex flex-wrap items-end gap-3">
          {running ? (
            <Button variant="danger" loading={m.endBreakIn.isPending} onClick={() => m.endBreakIn.mutate({ id: c.id, bid: running.id }, { onSuccess: () => void bis.refetch() })}>{t`End the break-in`}</Button>
          ) : (
            <Button disabled={!liveId} loading={m.breakIn.isPending} onClick={() => go({})}><Radio className="size-4" aria-hidden />{t`Switch to live now`}</Button>
          )}
          <Field id="bi-start" label={t`Or schedule from`}><Input type="datetime-local" value={start} onChange={(e) => setStart(e.target.value)} dir="ltr" /></Field>
          <Field id="bi-end" label={t`Until`}><Input type="datetime-local" value={end} onChange={(e) => setEnd(e.target.value)} dir="ltr" /></Field>
          <div className="mb-4"><Button variant="secondary" disabled={!liveId || !start} onClick={() => go({ start_at: new Date(start).toISOString(), end_at: end ? new Date(end).toISOString() : undefined })}>{t`Schedule`}</Button></div>
        </div>
      ) : null}
      <ul className="space-y-1 text-sm">
        {(bis.data?.data ?? []).map((b) => (
          <li key={b.id} className="flex flex-wrap items-center gap-2">
            <Badge tone={b.cancelled ? 'neutral' : b === running ? 'live' : 'primary'}>{b.cancelled ? t`Cancelled` : b === running ? t`On air` : b.kind === 'scheduled' ? t`Scheduled` : t`Live`}</Badge>
            <bdi className="ltr-isolate">{fmtDateTime(b.start_at, o)}{b.end_at ? ` – ${fmtDateTime(b.end_at, o)}` : ''}</bdi>
            {canOp && !b.cancelled && b !== running && Date.parse(b.start_at) > Date.now() ? (
              <Button size="sm" variant="link" onClick={() => m.endBreakIn.mutate({ id: c.id, bid: b.id }, { onSuccess: () => void bis.refetch() })}>{t`Cancel`}</Button>
            ) : null}
          </li>
        ))}
      </ul>
    </div>
  );
}

// --- settings ---------------------------------------------------------------------------------------------------------

function SettingsPanel({ c, onDeleted }: { c: FastChannel; onDeleted: () => void }) {
  const { t } = useLingui();
  const { tenant, scopes } = useTenant();
  const toast = useToast();
  const m = useFastMutations(tenant.slug);
  const live = useChannels(tenant.slug);
  const [title, setTitle] = React.useState(c.title);
  const [windowS, setWindowS] = React.useState(c.window_s);
  const [policy, setPolicy] = React.useState(c.ladder_policy);
  const [ssaiCh, setSsaiCh] = React.useState(c.ssai_channel_id ?? '');
  const [liveCh, setLiveCh] = React.useState(c.live_channel_id ?? '');
  const patch = (body: Parameters<typeof m.patch.mutate>[0]['body'], ok = t`Saved`) => m.patch.mutate({ id: c.id, body }, {
    onSuccess: () => toast.push({ title: ok, tone: 'ok' }),
    onError: (err) => toast.push({ title: t`Could not save`, description: problemText(err), tone: 'danger' }),
  });
  const opts = [{ value: 'none', label: t`None` }, ...(live.data ?? []).filter((x) => (x as { ladder?: string }).ladder === c.ladder).map((x) => ({ value: x.id ?? '', label: x.title || x.slug || '' }))];
  const ro = !can(scopes, WRITE);
  return (
    <div className="max-w-2xl space-y-4">
      <fieldset disabled={ro} className="space-y-3">
        <label className="flex items-center gap-2 text-sm"><Switch checked={c.enabled} onCheckedChange={(v) => patch({ enabled: v }, v ? t`The channel is on air` : t`The channel is off air`)} />{t`On air (the stream URL answers)`}</label>
        <label className="flex items-center gap-2 text-sm"><Switch checked={c.epg_enabled} onCheckedChange={(v) => patch({ epg_enabled: v })} />{t`List it in the guide feeds (XMLTV / JSON)`}</label>
        <label className="flex items-center gap-2 text-sm"><Switch checked={c.ssai_enabled} onCheckedChange={(v) => patch({ ssai_enabled: v, ssai_channel_id: ssaiCh })} />{t`Server-side ads at the breaks (SSAI)`}</label>
        <div className="grid gap-2 sm:grid-cols-2">
          <div className="mb-4">
            <span className="mb-1 block text-sm font-medium">{t`Ad settings from live channel`}</span>
            <Select ariaLabel={t`Ad settings from live channel`} value={ssaiCh || 'none'} onValueChange={(v) => setSsaiCh(v === 'none' ? '' : v)} options={opts} />
          </div>
          <div className="mb-4">
            <span className="mb-1 block text-sm font-medium">{t`Default break-in channel`}</span>
            <Select ariaLabel={t`Default break-in channel`} value={liveCh || 'none'} onValueChange={(v) => setLiveCh(v === 'none' ? '' : v)} options={opts} />
          </div>
          <Field id="fs-title" label={t`Name`}><Input value={title} onChange={(e) => setTitle(e.target.value)} /></Field>
          <Field id="fs-window" label={t`Live window (seconds)`} hint="30–3600"><Input type="number" min={30} max={3600} value={windowS} onChange={(e) => setWindowS(Number(e.target.value))} dir="ltr" /></Field>
          <div className="mb-4">
            <span className="mb-1 block text-sm font-medium">{t`Videos of another ladder`}</span>
            <Select ariaLabel={t`Videos of another ladder`} value={policy} onValueChange={(v) => setPolicy(v as 'exclude' | 'flag')}
              options={[{ value: 'exclude', label: t`Leave them out` }, { value: 'flag', label: t`Place them; the checker reports them` }]} />
          </div>
          <Field id="fs-ladder" label={t`Ladder`} hint={t`Every item must have this ladder's renditions.`}><Input value={c.ladder} readOnly dir="ltr" /></Field>
        </div>
        <Button onClick={() => patch({ title, window_s: windowS, ladder_policy: policy, ssai_channel_id: ssaiCh, live_channel_id: liveCh })} loading={m.patch.isPending}>{t`Save`}</Button>
      </fieldset>
      {!ro ? (
        <Button variant="danger" size="sm" onClick={() => { if (window.confirm(t`Delete this FAST channel with its schedules?`)) m.remove.mutate(c.id, { onSuccess: onDeleted }); }}>
          <Trash2 className="size-4" aria-hidden />{t`Delete channel`}
        </Button>
      ) : null}
    </div>
  );
}
